import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase, signOut } from '@/lib/supabase';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { ShieldAlert } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ComponentsPage } from '@/pages/ComponentsPage';
import { ComponentDetailPage } from '@/pages/ComponentDetailPage';
import { BuildsPage } from '@/pages/BuildsPage';
import { BuildDetailPage } from '@/pages/BuildDetailPage';
import { ReservationsPage } from '@/pages/ReservationsPage';
import { ReservationDetailPage } from '@/pages/ReservationDetailPage';
import { AssemblyOrdersPage } from '@/pages/AssemblyOrdersPage';
import { TechnicianOrdersPage } from '@/pages/TechnicianOrdersPage';
import type { User } from '@supabase/supabase-js';
import { ProductionPage } from '@/pages/ProductionPage';
import { StockIOPage } from '@/pages/StockIOPage';
import { PAODetailPage } from '@/pages/PAODetailPage';
import { AdminPage } from '@/pages/AdminPage';
import { TechnicianDashboardPage } from '@/pages/TechnicianDashboardPage';
import { ManagerDashboardPage } from '@/pages/ManagerDashboardPage';
import { LogsPage } from '@/pages/LogsPage';
import { UserActivityPage } from '@/pages/UserActivityPage';
import { ComparePage } from '@/pages/ComparePage';
import { ConfiguratorPage } from '@/pages/ConfiguratorPage';
import { QuotePage } from '@/pages/QuotePage';
import { PrintLabelsPage } from '@/pages/PrintLabelsPage';
import { DemoStationsPage } from '@/pages/DemoStationsPage';
import { DemoStationDetailPage } from '@/pages/DemoStationDetailPage';
import { AdCampaignsPage } from '@/pages/AdCampaignsPage';
import { VideoPlaylistsPage } from '@/pages/VideoPlaylistsPage';
import { MentionsPage } from '@/pages/MentionsPage';
import { WallPlannerPage } from '@/pages/WallPlannerPage';
import { SalesReportPage } from '@/pages/SalesReportPage';

import { canAccessVideoPlaylists, type UserRole as Role } from '@/lib/roles';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useActivityHeartbeat } from '@/lib/activity';
// Nullable here: no role is known until the profile query resolves.
type UserRole = Role | null;

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Shown when an authenticated account has no role - either it was never linked to a
// technician record, or that record has been marked inactive
function NoAccess({ email, reason }: { email?: string; reason: 'unprovisioned' | 'revoked' }) {
  const revoked = reason === 'revoked';
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
      <div className="w-full max-w-sm text-center">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 shadow-sm dark:shadow-2xl">
          <ShieldAlert className="h-8 w-8 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
          <h1 className="text-gray-900 dark:text-white text-xl font-semibold mb-1">
            {revoked ? 'Access disabled' : 'Account not set up'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
            {email ? <span className="font-medium">{email}</span> : 'This account'} signed in successfully, but{' '}
            {revoked
              ? 'no longer has access to the AOI Hub.'
              : 'has no access to the AOI Hub yet.'}{' '}
            Contact an administrator if you think this is a mistake.
          </p>
          <Button variant="outline" className="w-full" onClick={() => signOut()}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

// Protected route wrapper that waits for role
function ProtectedRoute({ 
  children, 
  allowedRoles, 
  userRole,
  isLoading,
  fallbackPath,
  allowed,
}: { 
  children: React.ReactNode; 
  allowedRoles?: UserRole[]; 
  userRole: UserRole;
  isLoading: boolean;
  fallbackPath: string;
  /** Replaces the allowedRoles check, for pages that are also granted per person. */
  allowed?: boolean;
}) {
  // Still loading role, show spinner
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // No role or not allowed
  if (!userRole || !(allowed ?? allowedRoles?.includes(userRole))) {
    return <Navigate to={fallbackPath} replace />;
  }
  
  return <>{children}</>;
}

// Smart redirect based on role
function RoleBasedRedirect({ userRole, isLoading }: { userRole: UserRole; isLoading: boolean }) {
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // Redirect based on role
  if (userRole === 'technician') {
    return <Navigate to="/my-dashboard" replace />;
  }
  if (userRole === 'manager') {
    return <Navigate to="/team-dashboard" replace />;
  }
  if (userRole === 'marketing') {
    return <Navigate to="/ad-campaigns" replace />;
  }
  if (userRole === 'retail') {
    return <Navigate to="/builds" replace />;
  }
  if (userRole === 'retail_shop') {
    return <Navigate to="/demo-stations" replace />;
  }

  // Admin and super_admin go to dashboard
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  useLocation(); // keep router context active

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch current user's role — gated on the session so it waits for auth to
  // resolve. See @/lib/useCurrentUser.
  const { data: userData, isLoading: roleLoading } = useCurrentUser({ enabled: !!user });

  const userRole = (userData?.role ?? null) as UserRole;
  const isRoleLoading = roleLoading && !!user;

  // Report presence for the User Activity page. Gated on a resolved role, so
  // an unprovisioned or deactivated account - which gets the dead end below
  // rather than the app - is not recorded as having used it.
  useActivityHeartbeat(!!user && !!userRole);

  // Full page loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Not logged in - show login
  if (!user) {
    return (
      <>
        <LoginPage />
        <Toaster />
      </>
    );
  }

  // Logged in but still loading role - show layout with loading state
  // This prevents flash of wrong content
  if (isRoleLoading) {
    return (
      <>
        <Layout>
          <LoadingSpinner />
        </Layout>
        <Toaster />
      </>
    );
  }

  // Authenticated, but no role was granted - unprovisioned or deactivated.
  // Render a dead end rather than falling through to the routes, which would
  // bounce between ProtectedRoute and its fallbackPath forever.
  if (!userRole) {
    return (
      <>
        <NoAccess email={user.email} reason={userData?.denied === 'revoked' ? 'revoked' : 'unprovisioned'} />
        <Toaster />
      </>
    );
  }

  // Determine fallback path based on role
  const getFallbackPath = () => {
    if (userRole === 'technician') return '/my-dashboard';
    if (userRole === 'manager') return '/team-dashboard';
    if (userRole === 'marketing') return '/ad-campaigns';
    if (userRole === 'retail') return '/builds';
    if (userRole === 'retail_shop') return '/demo-stations';
    return '/dashboard';
  };

  return (
    <>
      <Layout>
        <Routes>
          {/* Root redirect based on role */}
          <Route path="/" element={<RoleBasedRedirect userRole={userRole} isLoading={isRoleLoading} />} />
          
          {/* Admin Dashboard - super_admin and admin only */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute 
                allowedRoles={['super_admin', 'admin']} 
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath="/my-dashboard"
              >
                <DashboardPage />
              </ProtectedRoute>
            } 
          />

          {/* Technician Dashboard */}
          <Route 
            path="/my-dashboard" 
            element={
              <ProtectedRoute 
                allowedRoles={['technician']} 
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath={getFallbackPath()}
              >
                <TechnicianDashboardPage />
              </ProtectedRoute>
            } 
          />

          {/* Manager Dashboard */}
          <Route 
            path="/team-dashboard" 
            element={
              <ProtectedRoute 
                allowedRoles={['manager', 'super_admin', 'admin']} 
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath={getFallbackPath()}
              >
                <ManagerDashboardPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin/Super Admin only routes */}
          <Route 
            path="/production" 
            element={
              <ProtectedRoute 
                allowedRoles={['super_admin', 'admin']} 
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath={getFallbackPath()}
              >
                <ProductionPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/components"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager', 'technician']}
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath={getFallbackPath()}
              >
                <ComponentsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/components/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager', 'technician']}
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath={getFallbackPath()}
              >
                <ComponentDetailPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/stock-io"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager', 'technician']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <StockIOPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock-io/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager', 'technician']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <PAODetailPage />
              </ProtectedRoute>
            }
          />

          {/* Super Admin only */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <LogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <UserActivityPage />
              </ProtectedRoute>
            }
          />

          {/* Sales Report — admins and managers (carries cost and margin) */}
          <Route
            path="/sales-report"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <SalesReportPage />
              </ProtectedRoute>
            }
          />

          {/* Compare Builds — admin only */}
          <Route
            path="/compare"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <ComparePage />
              </ProtectedRoute>
            }
          />

          {/* Customer Quote — admin only */}
          <Route
            path="/quote"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <QuotePage />
              </ProtectedRoute>
            }
          />

          {/* Build Configurator — admin only */}
          <Route
            path="/configurator"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <ConfiguratorPage />
              </ProtectedRoute>
            }
          />

          {/* Print Labels — admin + manager + technician */}
          <Route
            path="/print-labels"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager', 'technician']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <PrintLabelsPage />
              </ProtectedRoute>
            }
          />

          {/* All roles can access */}
          <Route path="/builds" element={<BuildsPage />} />
          <Route path="/builds/new" element={<BuildDetailPage />} />
          <Route path="/builds/:id" element={<BuildDetailPage />} />
          <Route
            path="/reservations"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <ReservationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'manager']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <ReservationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/assembly-orders" 
            element={
              <ProtectedRoute 
                allowedRoles={['super_admin', 'admin', 'manager']} 
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath="/my-orders"
              >
                <AssemblyOrdersPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-orders" 
            element={
              <ProtectedRoute 
                allowedRoles={['technician']} 
                userRole={userRole} 
                isLoading={isRoleLoading} 
                fallbackPath="/assembly-orders"
              >
                <TechnicianOrdersPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Demo Stations — admins manage all; marketing sees Other category;
              technicians/retail can download the agent */}
          <Route
            path="/demo-stations"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'marketing', 'technician', 'retail', 'retail_shop']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <DemoStationsPage />
              </ProtectedRoute>
            }
          />

          {/* Demo Station Detail */}
          <Route
            path="/demo-stations/:id"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'marketing', 'technician', 'retail', 'retail_shop']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <DemoStationDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Ad Campaigns */}
          <Route
            path="/ad-campaigns"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'marketing']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <AdCampaignsPage />
              </ProtectedRoute>
            }
          />

          {/* Video Playlists */}
          <Route
            path="/video-playlists"
            element={
              <ProtectedRoute
                allowed={canAccessVideoPlaylists(userRole, userData?.canManagePlaylists ?? false)}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <VideoPlaylistsPage />
              </ProtectedRoute>
            }
          />

          {/* Social Mentions */}
          <Route
            path="/mentions"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin', 'marketing']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <MentionsPage userRole={userRole} />
              </ProtectedRoute>
            }
          />

          {/* Wall Planner (per-location) */}
          <Route
            path="/demo-stations/wall-planner/:locationId"
            element={
              <ProtectedRoute
                allowedRoles={['super_admin', 'admin']}
                userRole={userRole}
                isLoading={isRoleLoading}
                fallbackPath={getFallbackPath()}
              >
                <WallPlannerPage />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to appropriate dashboard */}
          <Route path="*" element={<RoleBasedRedirect userRole={userRole} isLoading={isRoleLoading} />} />
        </Routes>
      </Layout>
      <Toaster />
    </>
  );
}

export default App;
